/**
 *  Singleton acting as a global HTTP Interceptor
 *
 *        - signature's method follows Axios configuration [1]
 *
 *  @link https://gist.github.com/moreta/fb2625c59aa788009b1f7ce8e44ac559 [1]
 */
import Console from '~/utils/lib/core/console'
import { always, clone, prop, toString, tryCatch } from '~/utils/lib/core/prelude'
import APIError from '~/utils/infra/hamac/error'
import { causePrism, dataPrism, messagePrism, requestResponsePrism } from '~/utils/infra/hamac/request'
import { RetryManager } from '~/utils/infra/common/retry-manager'
import { MICRO_SERVICE_ERRORS } from '~/utils/infra/hamac/types'
import { ERRORS } from '~/utils/infra/account/types'

const console = new Console('plugin:hamac:interceptor')

export default {
  /**
   * @function request
   * @param {import('axios').Axios["interceptors"]["request"]} config
   * @promise-never-rejects
   * @return {Promise<import('axios').Axios["interceptors"]["request"]>}
   */
  request(config) {
    return Promise.resolve(config)
  },
  /**
   * @function requestError
   * @param {import('axios').AxiosError} error
   * @promise-always-rejects
   * @return {Promise<import('axios').AxiosError>}
   */
  requestError(error) {
    console.warn('request error captured', error)

    return Promise.reject(error)
  },
  /**
   * @function response
   * @this {import('./request').default}
   * @param {import('./types').RequestResponse} response
   * @promise-never-rejects
   * @return {Promise<import('./types').RequestResponse["data"]>}
   */
  response(response) {
    // Store returned result as the request response
    this.response = clone(response)

    // (?) "204 No Content" response should return `undefined` data instead
    //     of `null` (which can lead to horrendous handling, eg. w/ function
    //     argument default value)
    if (this.response.status === 204)
      this.response.data = undefined

    return Promise.resolve(this.response.data)
  },
  /**
   * @function responseError
   * @this {import('./request').default}
   * @param {import('./types').RequestError} error
   * @promise-always-rejects
   * @returns {Promise<*>} `APIError`
   */
   responseError(error) {
    // Parsing the promised returned <APIError>
    try {
      const response = requestResponsePrism(error)
      const status = prop('status', response)

      const cause = tryCatch(
        causePrism,
        always(MICRO_SERVICE_ERRORS.MISC)
      )(response)

      const message = tryCatch(
        messagePrism,
        always(`Une erreur serveur est survenue`)
      )(response)

      const payload = tryCatch(
        dataPrism,
        always(null)
      )(response)

      /// @NOTE: cloning issue resolved by mutation assignation
      ///     ↳ implication: don't know yet
      // this.response = clone(response)
      this.response = response
      // Store returned error as the request response
      // (the error handling is manageable via the <Promise> `Either` pattern of
      // the `Request.exec()`)
      this.error = new APIError(cause, message, payload)

      if (status === 401)
        return this.accept(RetryManager)

      return Promise.reject( this.error )

    } catch (/** @type {any} */ err) {

      logResponseError(err)

      this.error = new APIError(
        MICRO_SERVICE_ERRORS.MISC,
        'Une erreur serveur est survenue'
      )

      return Promise.reject( this.error )
    }
  }
}
/**
 * @function logResponseError
 * @static
 * @param {Error|APIError} err
 * @return {void}
 */
function logResponseError(err) {
  switch (true) {

    case
      (  err instanceof Error
      && toString(err) === ERRORS.REQUEST_RESPONSE_UNKNOWN_SCHEMA
      ):

      console.warn('caught a <RequestResponse> with unknown schema', err)
      break

    default:

      console.log('caught lambda <RequestError>', err)
      break
  }
}
